// The main thread's side of the calc Web Worker (worker.ts): a promise per
// request. Requests go in the order they are made and the worker answers
// them in that order, so a command sees everything sent before it.
//
//   const calc = CalcClient.worker()
//   const [a] = await calc.run([{ cmd: 'set_cells', cells: [['시트1', 0, 0, '=1+2']] }])
//
// Tests use CalcClient.inline(lib) with the library loaded in node.
import type { CalcAnswer, CalcCommand, CalcReply, CalcRequest } from './protocol'
import type { CalcLib } from './wasi'

/** What the client needs of a worker. */
export interface CalcPort {
  postMessage(m: CalcRequest): void
  onmessage: ((ev: MessageEvent<CalcReply>) => void) | null
  onerror?: ((ev: ErrorEvent) => void) | null
  terminate?(): void
}

export class CalcError extends Error {}

export class CalcClient {
  private next = 1
  private waiting = new Map<number, { resolve: (a: CalcAnswer[]) => void; reject: (e: Error) => void }>()

  constructor(private port: CalcPort) {
    port.onmessage = (ev) => {
      const r = ev.data
      const w = this.waiting.get(r.id)
      if (!w) return
      this.waiting.delete(r.id)
      if ('fatal' in r) w.reject(new CalcError(r.fatal))
      else w.resolve(r.answers)
    }
    port.onerror = (ev) => {
      ev.preventDefault?.()
      const err = new CalcError(ev.message || 'calc worker failed')
      for (const w of this.waiting.values()) w.reject(err)
      this.waiting.clear()
    }
  }

  /** A new Web Worker with its own workbook. */
  static worker(): CalcClient {
    const w = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'calc' })
    return new CalcClient(w as unknown as CalcPort)
  }

  /** The library on this thread (tests): the same order and answers. */
  static inline(lib: CalcLib): CalcClient {
    const port: CalcPort = {
      onmessage: null,
      postMessage(m) {
        queueMicrotask(() => {
          const answers = m.cmds.map((c) => {
            try {
              return lib.call(c) as CalcAnswer
            } catch (e) {
              return { ok: false, error: e instanceof Error ? e.message : String(e) }
            }
          })
          port.onmessage?.({ data: { id: m.id, answers } } as MessageEvent<CalcReply>)
        })
      },
    }
    return new CalcClient(port)
  }

  /** Runs the commands in order; one answer each. */
  run(cmds: CalcCommand[]): Promise<CalcAnswer[]> {
    if (cmds.length === 0) return Promise.resolve([])
    const id = this.next++
    return new Promise((resolve, reject) => {
      this.waiting.set(id, { resolve, reject })
      this.port.postMessage({ id, cmds })
    })
  }

  async call(cmd: CalcCommand): Promise<CalcAnswer> {
    const [a] = await this.run([cmd])
    return a ?? { ok: false, error: 'no answer' }
  }

  /** "=A1+$B$1" moved by (drow, dcol) as Excel copies it; null when it does not parse. */
  async translate(formula: string, drow: number, dcol: number): Promise<string | null> {
    const a = await this.call({ cmd: 'translate', formula, drow, dcol })
    return a.ok && typeof a.formula === 'string' ? a.formula : null
  }

  /** Many translations in one request. */
  async translateAll(items: { formula: string; drow: number; dcol: number }[]): Promise<(string | null)[]> {
    const answers = await this.run(items.map((t) => ({ cmd: 'translate' as const, ...t })))
    return answers.map((a) => (a.ok && typeof a.formula === 'string' ? a.formula : null))
  }

  terminate(): void {
    this.port.terminate?.()
    const err = new CalcError('calc stopped')
    for (const w of this.waiting.values()) w.reject(err)
    this.waiting.clear()
  }
}
